import * as React from "react";
import {
  Modal,
  Card,
  Input,
  Form,
  Button,
  Upload,
  message,
  Typography,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";

import { downloadPhonebookSample } from "../../services/commonController";
import {
  createSolveesyPhonebook,
  getSamplePhonebook,
} from "../../services/adminController";

const { Text } = Typography;

type Props = {
  status: boolean;
  onCancel: () => void;
  refetchPhonebooks: () => void;
};

export const CreateCampaignModal: React.FC<Props> = ({
  status,
  onCancel,
  refetchPhonebooks,
}) => {
  const [form] = Form.useForm();
  const [loanIds, setLoanIds] = React.useState<string[]>([]);
  const [fileList, setFileList] = React.useState<any[]>([]);

  const { isFetching } = useQuery({
    queryKey: ["sampleExcel"],
    queryFn: getSamplePhonebook,
    enabled: false,
  });

  React.useEffect(() => {
    if (status) {
      form.resetFields();
      setLoanIds([]);
      setFileList([]);
    }
  }, [status, form]);

  const { mutate, isPending } = useMutation({
    mutationFn: createSolveesyPhonebook,
    onSuccess: async () => {
      message.success("Phonebook created successfully!");

      form.resetFields();
      setLoanIds([]);
      setFileList([]);

      onCancel();
      await refetchPhonebooks();
    },
    onError: () => {
      message.error("Failed to create phonebook.");
    },
  });

  const onFinish = (values: any) => {
    if (loanIds.length === 0) {
      message.warning("No loan IDs found in uploaded file.");
      return;
    }

    mutate({
      phonebookname: values.name,
      loanIds,
    });
  };

  const handleExcel = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const extractedLoanIds = jsonData
        .map((row: any) => row.loanId)
        .filter((id: any) => id !== undefined && id !== "");

      setLoanIds(extractedLoanIds);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleDownloadSample = async () => {
    try {
      const response = await downloadPhonebookSample();

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "Sample_excel_template.xlsx";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      message.error("Download failed.");
    }
  };

  return (
    <Modal
      width={800}
      open={status}
      footer={null}
      onCancel={isPending ? undefined : onCancel}
      maskClosable={!isPending}
      keyboard={!isPending}
      destroyOnClose
    >
      <Card title="Create Phonebook">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={isPending}
        >
          <Form.Item
            label="Phonebook Info"
            name="name"
            rules={[
              {
                required: true,
                message: "Please input your phonebook name!",
              },
            ]}
          >
            <Input placeholder="Enter Phonebook Name" />
          </Form.Item>

          <Form.Item label="Upload Excel File">
            <Text type="secondary" className="text-xs text-gray-500">
              We recommend you to{" "}
              <Button
                size="small"
                type="link"
                onClick={handleDownloadSample}
                loading={isFetching}
                disabled={isPending}
              >
                Download sample Excel template
              </Button>{" "}
              and upload loan IDs in the same format to avoid errors.
            </Text>

            <Upload
              beforeUpload={(file) => {
                handleExcel(file);
                setFileList([file]);
                return false;
              }}
              fileList={fileList}
              onRemove={() => {
                setFileList([]);
                setLoanIds([]);
              }}
              maxCount={1}
              accept=".xlsx,.xls"
              disabled={isPending}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={isPending}
              >
                Browse Excel
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <div className="flex justify-center space-x-4">
              <Button
                type="primary"
                htmlType="submit"
                className="w-[20%] mr-3"
                loading={isPending}
                disabled={isPending}
              >
                Submit
              </Button>

              <Button
                type="default"
                onClick={onCancel}
                className="w-[20%]"
                disabled={isPending}
              >
                Cancel
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
};